import dayjs from "dayjs";
import { Icon } from "@iconify/react";
import { Table } from "@/components/table";
import { useTranslation } from "react-i18next";
import { useCompanyMembers } from "@/hooks";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import React, { useMemo } from "react";

interface TeamMember {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  photo: string | null;
  status: string;
  createdAt: string;
}

const TeamMembersTable: React.FC = () => {
  const { t } = useTranslation();
  const { data: membersData, isLoading } = useCompanyMembers();

  const members: TeamMember[] = (membersData as TeamMember[] | undefined) || [];

  const formattedMembers = useMemo(() => {
    return members.map((member: TeamMember, index: number) => {
      const fullName = member.firstName && member.lastName
        ? `${member.firstName} ${member.lastName}`
        : member.email;
      const initials = member.firstName && member.lastName
        ? `${member.firstName[0]}${member.lastName[0]}`
        : member.email.substring(0, 2).toUpperCase();

      return {
        ...member,
        index: index + 1,
        fullName,
        initials,
        joinDate: member.createdAt
          ? dayjs(member.createdAt).format("MMM D, YYYY")
          : "",
        avatar: (
          <Avatar className="w-8 h-8">
            <AvatarImage src={member.photo || undefined} alt={fullName} />
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
        ),
      };
    });
  }, [members]);

  const columns = [
    { key: "avatar", label: "", width: 60 },
    { key: "fullName", label: t("company.team.columns.name"), width: 200 },
    { key: "email", label: t("company.team.columns.email"), width: 250 },
    { key: "status", label: t("company.team.columns.status"), width: 120 },
    { key: "joinDate", label: t("company.team.columns.joinDate"), width: 150 },
  ];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon icon="ic:round-people" className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-gray-600">{t("company.team.empty")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Table
        data={formattedMembers}
        columns={columns}
        title={t("company.team.title")}
        exportable
        isLoading={isLoading}
      />
    </div>
  );
};

export default TeamMembersTable;

